/**
 * `advisorPanel` — the pure client-side plumbing behind the browser advisor panel. Two
 * responsibilities, both PURE (no React, no hooks, no DOM):
 *
 *   1. **Rank** ({@link rankAdvisorPlans}) — a thin wrapper over `@lancer-vtt/ai`'s
 *      `rankMacroMoves`, reshaping each `RankedMacroMove` into the exact display-ready row the
 *      panel view renders: the one-line `reasoning`, the per-term evaluator breakdown, and
 *      a `collapsed: true` default — expressed as DATA on the row, never behavior, so this pure
 *      module owns no UI state and the view has nothing to compute.
 *   2. **Arm** ({@link planToArmedAction}) — maps a ranked `MacroMove` to the `ActionDraft`
 *      CombatApp's existing `aim` state already understands (`setAim`), plus the move leg's
 *      destination (`world.key`-encoded, for the panel's move-ghost). Follows the "arm it, human
 *      fires" precedent `CombatApp.tsx`'s `startOverwatchTake` set (a fixed target + board FIRE
 *      button, never an inline dispatch), and is the CLI's `planVerbs` translation
 *      (`cli/src/encounter-cli.ts`) lifted one layer up to the aim model. Reuses the shipped
 *      `buildAttackIntent` (`./attackIntent.ts`) and `world.key` (`worldFor`,
 *      `@lancer-vtt/domain`) — never re-derives an intent.
 *
 * **Only ranks the combatant that is ALREADY `state.activation.turn.actor`.** `generateMacroMoves`
 * itself returns `[]` for anyone else, and `activationPick.ts`'s `withHypotheticalActivation`
 * projection (the sanctioned way to rank a not-yet-activated unit) is NOT wired here.
 *
 * **Never fires anything.** {@link planToArmedAction} only produces a draft; firing rides the
 * existing, unchanged `setAim`/`sendIntent`/`dispatchMove` wall CombatApp already drives.
 */
import { worldFor, type EncounterState } from "@lancer-vtt/domain";
import type { Compendium } from "@lancer-vtt/compendium";
import type { LoadedOverlay } from "@lancer-vtt/rules";
import { rankMacroMoves, type FeatureTerm, type MacroMove, type RankedMacroMove, type TermContribution } from "@lancer-vtt/ai";
import { cellLabel } from "./cellLabel";
import type { ActionDraft } from "./combatInspector";

/**
 * One ranked plan, reshaped for the panel view — a pure display row, no behavior. `ranked` keeps
 * the full underlying `RankedMacroMove` (score/positionBreakdown/attackEv/move) for
 * {@link planToArmedAction} and anything the view needs beyond the display fields below.
 */
export type AdvisorPanelPlan = {
  /** Stable per-plan key — the underlying `MacroMove.id` (`generator.ts`), safe for a React list key. */
  id: string;
  /**
   * The plan's one-line reasoning (label + hit%/E[dmg]/score when armed with an attack).
   * **Composed here rather than copied from `RankedMacroMove.reasoning`** — it mirrors the advisor's
   * own sentence exactly (`advisor.ts`'s `reasoning` over `generator.ts`'s `label`) and diverges in
   * one respect only: a cell is named the way the BOARD names it (`cellLabel` → `G5`), not the raw
   * `world.key` pair (`6,4`) the AI package bakes into its label. See {@link planReasoning}.
   */
  reasoning: string;
  /** The plan's total utility score — rows arrive pre-sorted highest-first (the advisor's own order). */
  score: number;
  /** The projected end-state's per-term evaluator breakdown (position-swap terms — NOT the attack leg's own EV numbers, which live on `ranked.attackEv`). */
  terms: TermContribution[];
  /**
   * Whether this row's `terms` breakdown starts folded — always `true`. A plain data field, not a
   * UI `useState`, so the panel view has nothing to compute on mount.
   */
  collapsed: boolean;
  /** The full advisor output this row reshapes — `move`/`score`/`positionBreakdown`/`attackEv`/`reasoning`. */
  ranked: RankedMacroMove;
};

export type RankAdvisorPlansArgs = {
  state: EncounterState;
  actorId: string;
  compendium: Compendium;
  overlay?: LoadedOverlay;
  /** The weighted feature terms to evaluate projected end-states with; forwarded to `rankMacroMoves` (defaults to `DEATHMATCH_TERMS`). */
  terms?: readonly FeatureTerm[];
  /** How many top-ranked plans to return; forwarded to `rankMacroMoves` (default 5). */
  k?: number;
};

/**
 * Rank `actorId`'s legal macro-moves and reshape them into panel-ready rows. Returns `[]`
 * whenever `rankMacroMoves`/`generateMacroMoves` would (not `actorId`'s activation, no legal
 * moves, incapacitated, …) — the panel's own empty-state, not a special case here.
 */
export function rankAdvisorPlans(args: RankAdvisorPlansArgs): AdvisorPanelPlan[] {
  const { state, actorId, compendium, overlay, terms, k } = args;
  const ranked = rankMacroMoves({ state, actorId, compendium, overlay, terms, k });
  return ranked.map((r) => ({
    id: r.move.id,
    reasoning: planReasoning(state, r),
    score: r.score,
    terms: r.positionBreakdown.terms,
    collapsed: true,
    ranked: r,
  }));
}

/**
 * The plan's headline, mirroring `generator.ts`'s `MacroMove.label` **except** that a destination is
 * named the way the board's ruler names it (`cellLabel` → `Move to G5`) instead of the raw
 * `world.key` pair the generator bakes in (`Move to 6,4`). Every other word — the comma, the
 * lowercase `attack`, `Hold position` — is deliberately identical, so a reader comparing the panel
 * against `lvtt encounter suggest` sees ONE difference and knows what it is.
 *
 * Composed rather than string-patched over `move.label`: a `replace` of the raw key would silently
 * no-op the day the generator rephrases, quietly reverting the panel to raw coordinates.
 *
 * **This is a mirror of `generator.ts`'s `MacroMove.label` and must move in lockstep with it** —
 * the duplicate-sentence drift risk that comes with not string-patching. It is pinned, not merely
 * documented: `advisorPanel.test.ts` derives its expectation FROM the live `RankedMacroMove`, so any
 * upstream rewording reds that test instead of silently diverging from what the CLI prints.
 */
function planLabel(state: EncounterState, move: MacroMove): string {
  const destination = move.moved ? `Move to ${cellLabel(state, move.destination)}` : null;
  if (!move.attack) return destination ?? "Hold position";
  return `${destination === null ? "" : `${destination}, `}attack ${move.attack.target.name} with ${move.attack.weaponName}`;
}

/**
 * The full one-line row text — {@link planLabel} plus the advisor's own EV/score tail, mirroring
 * `advisor.ts`'s `reasoning` field verbatim (same numbers, same rounding, same punctuation).
 */
function planReasoning(state: EncounterState, ranked: RankedMacroMove): string {
  const label = planLabel(state, ranked.move);
  const ev = ranked.attackEv;
  if (!ev) return `${label} — score=${ranked.score.toFixed(2)}`;
  return `${label} — ${(ev.pHit * 100).toFixed(0)}% to hit (${(ev.pCrit * 100).toFixed(0)}% crit), E[dmg]=${ev.expectedDamage.toFixed(1)}, score=${ranked.score.toFixed(2)}`;
}

/**
 * The armed plan — what the panel hands to `setAim` (the attack leg) plus the move-ghost
 * destination (the move leg). Either half may be `null`: a plan with no attack leg (a move-only
 * or pure-hold plan) has nothing to arm as a skirmish draft; a plan that doesn't move (an
 * in-place "stay"/attack-only plan) has no destination to ghost.
 */
export type ArmedPlan = {
  /** The `ActionDraft` to `setAim(...)` — `null` when `move.attack` is absent. */
  aim: ActionDraft | null;
  /** The move leg's destination, `world.key`-encoded (matches `planVerbs`' `--to` value) — `null` when `move.moved` is `false`. */
  moveDestinationKey: string | null;
};

/**
 * Map a ranked `MacroMove` to the aim draft + move-ghost destination the panel arms on click.
 * The attack leg becomes a plain `"skirmish"` draft with a **fixed
 * target** (`fixedTargetId`, `CombatApp.startOverwatchTake`'s own precedent for "the target is
 * already known, don't make the human re-click the board") — no reaction, no ammo pre-choice, no
 * accuracy modifier, matching exactly what `generateMacroMoves` itself declared legal
 * (`RequestAttack.accuracy: 0`, no `profile`/`choices`). `advisorPanel.test.ts`'s honesty check
 * proves the intent this draft eventually fires through (CombatApp's existing `fireAt`,
 * untouched by this module) reproduces `move.attack.intent` byte for byte.
 */
export function planToArmedAction(state: EncounterState, move: MacroMove): ArmedPlan {
  const world = worldFor(state);
  const moveDestinationKey = move.moved ? world.key(move.destination) : null;
  const intent = move.attack?.intent;
  // A Superheavy plan is a Barrage (Core p.70): arm the Barrage draft with the plan's committed
  // mount and weapon. The Barrage draft has no fixed-target FIRE button, so the target is a board click.
  if (intent?.type === "request_barrage") {
    return { aim: { attackerId: move.actor, mode: "barrage", weaponId: intent.weapon, profileId: "", accuracy: 0, ...(intent.mounts ? { mounts: intent.mounts } : {}) }, moveDestinationKey };
  }
  const aim: ActionDraft | null = move.attack
    ? {
        attackerId: move.actor,
        mode: "skirmish",
        weaponId: move.attack.weapon,
        profileId: "",
        accuracy: 0,
        fixedTargetId: move.attack.target.id,
      }
    : null;
  return { aim, moveDestinationKey };
}
